/**
 * The visual language of a process diagram — one place, because the designer
 * and the runtime's read-only diagram must not drift.
 *
 * It lives in the RUNTIME package and the designer imports it, rather than the
 * other way round: the diagram is drawn on four surfaces (the Process Flow
 * designer, the platform Run view, the designer's live preview and a downloaded
 * copy of the app) and only one of them is the designer. When this file sat in
 * `frontend/src/pages/designers/processflow/` the runtime kept its own copy,
 * and the copy drifted — faint borders, a different fill, an edge routed from a
 * box's centre instead of its border — which is exactly the "arrows hanging in
 * space" the shared file existed to prevent.
 *
 * A box's SHAPE says what it is. That is the difference between a diagram you
 * read and a diagram you decode: a step is a rounded box, master data is a
 * stadium, a data store is the drum every database has been drawn as for fifty
 * years, another process is a box with a spine, and a party outside the
 * application is dashed because the application does not own it.
 *
 * Colour says what an ARROW does, and it is the same palette at both ends of
 * the product: blue is the next step, teal a related record, amber a write into
 * a store, violet a look-up.
 */

import type { NodeKind } from './contract'

export const NODE_W = 172
export const NODE_H = 52
/** Nodes land on this grid, so a diagram somebody dragged into shape stays in
 *  shape. Free positioning reads as sloppy at every zoom level. It is also the
 *  backdrop both canvases rule, at four times this. */
export const GRID = 8

export interface KindStyle {
  label: string
  /** One line, said the way a designer would ask for the thing. */
  hint: string
  stroke: string
  fill: string
  ink: string
}

/** Theme tokens, never hex: this panel is read in both themes, and a hardcoded
 *  slate on a dark ground is the grey-on-grey the fields used to be. */
export const KIND_STYLE: Record<NodeKind, KindStyle> = {
  step: {
    label: 'Step',
    hint: 'A screen where the work is done.',
    stroke: 'var(--color-border-strong, #c7cfd9)',
    fill: 'var(--color-bg-secondary, #fff)',
    ink: 'var(--color-text, #1c2430)',
  },
  entity: {
    label: 'Entity',
    hint: 'Master data — a customer, a supplier, an employee. The same kind of screen as a step; the process reads it rather than working through it.',
    stroke: '#7c3aed',
    fill: 'var(--color-bg-secondary, #fff)',
    ink: 'var(--color-text, #1c2430)',
  },
  store: {
    label: 'Data store',
    hint: 'A screen the process WRITES and nobody types into — stock, a ledger, a balance. Marking it one stops an arrow into it raising a task.',
    stroke: '#b45309',
    fill: 'var(--color-bg-secondary, #fff)',
    ink: 'var(--color-text, #1c2430)',
  },
  process: {
    label: 'Another process',
    hint: 'Hands the work over to a process drawn on its own diagram.',
    stroke: 'var(--color-accent, #0061D0)',
    fill: 'var(--color-bg-secondary, #fff)',
    ink: 'var(--color-text, #1c2430)',
  },
  external: {
    label: 'Outside party',
    hint: 'Somebody outside the application — a customer emailing, a bank.',
    stroke: 'var(--color-text-secondary, #465062)',
    fill: 'var(--color-bg-secondary, #fff)',
    ink: 'var(--color-text-secondary, #465062)',
  },
}

/** The caption over a box, for the kinds a reader cannot be expected to know
 *  by shape alone. A step needs none — it is the thing a process is made of. */
export const KIND_CAPTION: Partial<Record<NodeKind, string>> = {
  entity: 'Master data',
  store: 'Data store',
  process: 'Another process',
  external: 'Outside the app',
}

export interface EdgeStyle {
  label: string
  hint: string
  stroke: string
  dash?: string
  width: number
  authored: boolean
}

export const EDGE_STYLE: Record<string, EdgeStyle> = {
  work: {
    label: 'Next step',
    hint: 'What happens next, and who is asked to do it.',
    stroke: 'var(--color-accent, #0061D0)', width: 2, authored: true,
  },
  link: {
    label: 'Related record',
    hint: 'These two records belong together. Nobody is asked to do anything.',
    stroke: '#0f766e', width: 2, authored: true,
  },
  update: {
    label: 'Updates',
    hint: 'This step writes to that data store.',
    stroke: '#b45309', width: 2, dash: '7 3', authored: true,
  },
  read: {
    label: 'Reads',
    hint: 'This step looks that up.',
    stroke: '#7c3aed', width: 1.6, dash: '2 4', authored: true,
  },
  approval: {
    label: 'Approval', hint: 'Read from the object’s approval binding.',
    stroke: 'var(--color-text-secondary, #94a3b8)', dash: '5 4', width: 1.4,
    authored: false,
  },
  reference: {
    label: 'Reads (from the model)', hint: 'A lookup declared on a column.',
    stroke: 'var(--color-border-strong, #cbd5e1)', dash: '3 4', width: 1.4,
    authored: false,
  },
  cross_update: {
    label: 'Updates (from the model)', hint: 'A cross-update writing another object’s table.',
    stroke: 'var(--color-border-strong, #cbd5e1)', dash: '3 4', width: 1.4,
    authored: false,
  },
  event: {
    label: 'Integration event', hint: 'An event this object raises to IOFlow.',
    stroke: 'var(--color-border-strong, #cbd5e1)', dash: '3 4', width: 1.4,
    authored: false,
  },
}

export interface Box { x: number; y: number }

/**
 * Where an arrow between two boxes actually starts and ends.
 *
 * Centre-to-centre is the obvious thing and it is why the old canvas showed no
 * direction at all: the arrowhead landed at the middle of the target box, under
 * it, invisible. So the line is clipped to both BORDERS, and the head sits
 * against the box it points at, which is the only place it can be seen.
 */
export function edgeGeometry(a: Box, b: Box) {
  const ac = { x: a.x + NODE_W / 2, y: a.y + NODE_H / 2 }
  const bc = { x: b.x + NODE_W / 2, y: b.y + NODE_H / 2 }
  const p1 = borderPoint(a, ac, bc)
  const p2 = borderPoint(b, bc, ac, 7)
  // A gentle S between the two border points; a straight run where the boxes
  // sit on one row, because a curve there reads as a detour that isn't there.
  const flat = Math.abs(ac.y - bc.y) < 6
  const mx = (p1.x + p2.x) / 2
  const d = flat
    ? `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y}`
    : `M ${p1.x} ${p1.y} C ${mx} ${p1.y}, ${mx} ${p2.y}, ${p2.x} ${p2.y}`
  return {
    d,
    p1,
    p2,
    mid: { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 },
    angle: Math.atan2(p2.y - p1.y, p2.x - p1.x) * (180 / Math.PI),
  }
}

/** The point where the line from `from` to `to` leaves `box`, pulled back by
 *  `inset` so an arrowhead has somewhere to sit. */
function borderPoint(box: Box, from: { x: number; y: number },
                     to: { x: number; y: number }, inset = 0) {
  const dx = to.x - from.x
  const dy = to.y - from.y
  if (dx === 0 && dy === 0) return { ...from }
  const hw = NODE_W / 2
  const hh = NODE_H / 2
  // Scale the direction vector until it hits whichever side comes first.
  const sx = dx === 0 ? Infinity : hw / Math.abs(dx)
  const sy = dy === 0 ? Infinity : hh / Math.abs(dy)
  const s = Math.min(sx, sy)
  const len = Math.hypot(dx, dy)
  const back = inset / len
  return {
    x: from.x + dx * Math.max(s - back, 0),
    y: from.y + dy * Math.max(s - back, 0),
  }
}

export function clip(s: string, n: number): string {
  return s.length > n ? `${s.slice(0, n - 1)}…` : s
}

/**
 * The outline that says what a box IS — drawn once, so the designer's canvas
 * and the runtime's read-only diagram are the same picture.
 *
 * `stroke` is passed in rather than read from KIND_STYLE because every surface
 * overrides it for a state of its own: selected in the designer, active or
 * locked at run time. The SHAPE never changes, and that is the point.
 */
export function NodeOutline({ kind, x, y, stroke, fill, width = 1.4, dash }: {
  kind: NodeKind
  x: number
  y: number
  stroke: string
  fill: string
  width?: number
  /** Overrides the kind's own dash — a locked box at run time. */
  dash?: string
}) {
  const w = NODE_W
  const h = NODE_H
  if (kind === 'store') {
    // The drum every database has been drawn as: unmistakable at a glance, and
    // it is the whole reason a store does not need a caption to be understood.
    const ry = 8
    return (
      <g>
        <path
          d={`M ${x} ${y + ry} a ${w / 2} ${ry} 0 0 1 ${w} 0
              v ${h - ry * 2} a ${w / 2} ${ry} 0 0 1 ${-w} 0 z`}
          fill={fill} stroke={stroke} strokeWidth={width} strokeDasharray={dash} />
        <path d={`M ${x} ${y + ry} a ${w / 2} ${ry} 0 0 0 ${w} 0`}
          fill="none" stroke={stroke} strokeWidth={width} opacity={0.7} />
      </g>
    )
  }
  if (kind === 'entity') {
    return <rect x={x} y={y} width={w} height={h} rx={h / 2}
      fill={fill} stroke={stroke} strokeWidth={width} strokeDasharray={dash} />
  }
  if (kind === 'process') {
    return (
      <g>
        <rect x={x} y={y} width={w} height={h} rx={7}
          fill={fill} stroke={stroke} strokeWidth={width} strokeDasharray={dash} />
        <line x1={x + 6} y1={y} x2={x + 6} y2={y + h} stroke={stroke} strokeWidth={width} />
        <line x1={x + w - 6} y1={y} x2={x + w - 6} y2={y + h}
          stroke={stroke} strokeWidth={width} />
      </g>
    )
  }
  if (kind === 'external') {
    return <rect x={x} y={y} width={w} height={h} rx={7}
      fill={fill} stroke={stroke} strokeWidth={width} strokeDasharray={dash || '5 4'} />
  }
  return <rect x={x} y={y} width={w} height={h} rx={9}
    fill={fill} stroke={stroke} strokeWidth={width} strokeDasharray={dash} />
}

/** The arrowheads, one per edge kind, in the colour that kind is drawn in.
 *  Both canvases put this in their <defs> and point `markerEnd` at
 *  `url(#${prefix}-<kind>)`. */
export function EdgeArrowDefs({ prefix }: { prefix: string }) {
  return (
    <>
      {Object.entries(EDGE_STYLE).map(([kind, st]) => (
        <marker key={kind} id={`${prefix}-${kind}`} viewBox="0 0 10 10" refX="8.5" refY="5"
          markerWidth="7" markerHeight="7" orient="auto">
          <path d="M 0 1 L 9.5 5 L 0 9 z" fill={st.stroke} />
        </marker>
      ))}
    </>
  )
}

/** The ruled backdrop a diagram is drawn on. A blank field gives the eye no
 *  sense of scale when it pans, and makes a box's edge the only line on the
 *  screen — which is why an unruled canvas reads as arrows hanging in space. */
export function GridBackdrop({ id }: { id: string }) {
  return (
    <pattern id={id} width={GRID * 4} height={GRID * 4} patternUnits="userSpaceOnUse">
      <path d={`M ${GRID * 4} 0 L 0 0 0 ${GRID * 4}`} fill="none"
        stroke="var(--color-border, #dde3ea)" strokeWidth="0.6" opacity="0.55" />
    </pattern>
  )
}
